/**
 * ServiceDetailCard
 * -----------------
 * Full detail card shown when exactly one service is matched.
 * All data from props — edit service_registry.py only.
 *
 * Icons  → ./icons.jsx
 * Logic  → ./serviceUtils.jsx (Collapsible, TatTable, StepsList, DocsList)
 *
 * Props:
 *   service  {object}  - full service object from /api/chat/ response
 *   image    {object}  - optional image object from /api/chat/ response
 */

import { useEffect, useState } from "react";
import {
  ExternalLink, CloseIcon, LockIcon, CheckIcon,
  ImagePlaceholderIcon, ExpandIcon,
} from "./icons";
import { Collapsible, TatTable, StepsList, DocsList } from "./ServiceUtils";

// ── Lightbox ───────────────────────────────────────────────
function Lightbox({ image, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.88)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: "92vw", position: "relative" }}>
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: "-36px", right: 0,
            background: "rgba(255,255,255,0.15)", border: "none",
            color: "#fff", borderRadius: "6px", padding: "5px 10px",
            cursor: "pointer", display: "flex", alignItems: "center",
            gap: "5px", fontSize: "12px", fontFamily: "var(--font-sans)",
          }}
        >
          <CloseIcon /> Close
        </button>
        <img
          src={image.url} alt={image.title}
          onError={e => { e.target.style.display = "none"; }}
          style={{ maxWidth: "92vw", maxHeight: "82vh", borderRadius: "10px", display: "block" }}
        />
        {image.caption && (
          <p style={{ color: "#d1d5db", fontSize: "12px", marginTop: "10px", textAlign: "center", lineHeight: 1.5 }}>
            {image.caption}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────
export default function ServiceDetailCard({ service, image }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgLoaded,    setImgLoaded]    = useState(false);
  const [imgError,     setImgError]     = useState(false);

  useEffect(() => {
    setImgLoaded(false);
    setImgError(false);
  }, [image?.url]);

  const tat          = service.tat          || [];
  const steps        = service.steps        || [];
  const requiredDocs = service.required_docs || [];

  const hasImage   = !!image?.url;
  const canEnlarge = hasImage && imgLoaded && !imgError;

  return (
    <>
      <div style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: "var(--border-radius-lg)",
        borderTop: "3px solid #16a34a",
        maxWidth: "460px",
        marginTop: "8px",
        fontFamily: "var(--font-sans)",
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{ padding: "14px 16px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <span style={{ fontSize: "10px", fontWeight: "500", letterSpacing: "0.06em", textTransform: "uppercase", color: "#15803d", background: "#dcfce7", padding: "2px 8px", borderRadius: "4px" }}>
              OSR Service
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "var(--color-text-secondary)" }}>
              <LockIcon /> Login required to apply
            </span>
          </div>
          <h3 style={{ margin: "0 0 5px", fontSize: "15px", fontWeight: "500", color: "var(--color-text-primary)", lineHeight: 1.3 }}>
            {service.name}
          </h3>
          <p style={{ margin: "0 0 12px", fontSize: "13px", color: "var(--color-text-secondary)", lineHeight: 1.55 }}>
            {service.description}
          </p>
        </div>

        {/* Form image preview */}
        <div
          onClick={() => canEnlarge && setLightboxOpen(true)}
          style={{
            background: "var(--color-background-secondary)",
            minHeight: "100px",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: canEnlarge ? "pointer" : "default",
            position: "relative", overflow: "hidden",
            borderTop: "0.5px solid var(--color-border-tertiary)",
            borderBottom: "0.5px solid var(--color-border-tertiary)",
          }}
        >
          {(!hasImage || !imgLoaded || imgError) && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", padding: "24px 16px" }}>
              <div style={{ color: "var(--color-text-tertiary)" }}><ImagePlaceholderIcon /></div>
              <p style={{ fontSize: "11px", color: "var(--color-text-tertiary)", textAlign: "center", margin: 0 }}>
                {imgError || !hasImage ? "Form preview coming soon" : "Loading preview..."}
              </p>
            </div>
          )}
          {hasImage && (
            <img
              src={image.url}
              alt={image.title || service.name}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              style={{ width: "100%", maxHeight: "200px", objectFit: "cover", objectPosition: "top", display: imgLoaded && !imgError ? "block" : "none" }}
            />
          )}
          {canEnlarge && (
            <div style={{ position: "absolute", bottom: "8px", right: "8px", background: "rgba(0,0,0,0.5)", borderRadius: "5px", padding: "3px 7px", display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#fff" }}>
              <ExpandIcon /> Tap to enlarge
            </div>
          )}
          {canEnlarge && image.caption && (
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.45)", padding: "5px 10px" }}>
              <p style={{ fontSize: "10px", color: "#e5e7eb", margin: 0, lineHeight: 1.4 }}>{image.caption}</p>
            </div>
          )}
        </div>

        {/* Collapsible sections */}
        <div style={{ padding: "0 16px 4px" }}>

          {tat.length > 0 && (
            <Collapsible
              label={`Processing time (TAT)${tat.length > 1 ? ` — ${tat.length} customer types` : ""}`}
              defaultOpen={true}
            >
              <TatTable tat={tat} />
              <p style={{ margin: "6px 0 0", fontSize: "10px", color: "var(--color-text-tertiary)" }}>
                TAT starts from Day 0 — the day BPC accepts your complete application. Excludes weekends and public holidays.
              </p>
            </Collapsible>
          )}

          {steps.length > 0 && (
            <Collapsible label={`How it works — ${steps.length} steps`} defaultOpen={true}>
              <StepsList steps={steps} />
            </Collapsible>
          )}

          {requiredDocs.length > 0 ? (
            <Collapsible label={`Documents to upload — ${requiredDocs.length} required`} defaultOpen={true}>
              <DocsList docs={requiredDocs} />
            </Collapsible>
          ) : (
            <div style={{ borderTop: "0.5px solid var(--color-border-tertiary)", padding: "9px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#15803d", background: "#f0fdf4", border: "0.5px solid #bbf7d0", borderRadius: "var(--border-radius-md)", padding: "6px 10px" }}>
                <CheckIcon /> No documents to upload — just fill the form and submit
              </div>
            </div>
          )}

          {service.form_fields?.length > 0 && (
            <Collapsible label={`Form fields — ${service.form_fields.length} fields to fill`} defaultOpen={false}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {service.form_fields.map((field, i) => (
                  <div key={i} style={{ fontSize: "12px", color: "var(--color-text-secondary)", padding: "5px 8px", background: "var(--color-background-secondary)", borderRadius: "5px", lineHeight: 1.4 }}>
                    {field}
                  </div>
                ))}
              </div>
            </Collapsible>
          )}
        </div>

        {/* Apply button */}
        <div style={{ padding: "4px 16px 16px" }}>
          <a
            href={service.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "10px 0", background: "#16a34a", color: "#fff", borderRadius: "var(--border-radius-md)", fontSize: "13px", fontWeight: "500", textDecoration: "none", width: "100%" }}
          >
            Apply now on OSR portal <ExternalLink />
          </a>
        </div>
      </div>

      {lightboxOpen && image && (
        <Lightbox image={image} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
}
